import React from "react";
import { List, ListItemText, Badge, Drawer, Collapse, ListItemIcon, ListItemButton } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import RateReviewIcon from "@mui/icons-material/RateReview";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import PeopleIcon from "@mui/icons-material/People";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import PendingActionIcon from "../../icons/PendingAction";
import HomeIcon from "@mui/icons-material/Home";
import CheckinIcon from "../../icons/Checkin";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import SettingsIcon from "@mui/icons-material/Settings";
import ErrorIcon from "@mui/icons-material/Error";
import { sumArray } from "../../util/mathUtil";
import { receiveStatus } from "../../util/receiveStatus";
import { creditStatus } from "../../util/creditRequestMapping";
import { receiveFailureReason, failureReasonsRequiringInterventionCsv } from "../../util/receiveFailureReason";
import { BadgedMenuItem } from "./BadgedMenuItem";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import PageContext from "../Common/PageContext";
import { CreditCard, Label } from "@mui/icons-material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import ThumbsUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { isFeatureEnabledForLocation } from "../../util/featureUtil";
import { useStyles } from "./Menu.styles";
import { BadgedItem } from "./BadgedItem";
import { getTransferBadgeCount, transferBadgeStatus } from "../../util/badgeCount";

type DrawerMenuProps = {
	onMenuItemSelected: (index: string) => void;
	OnDrawerClose: () => void;
	isOpenMenu: boolean;
};
export const DrawerMenu = ({ onMenuItemSelected, OnDrawerClose, isOpenMenu }: DrawerMenuProps) => {
	const [pageContext] = React.useContext(PageContext);
	const {
		userLocation,
		featureConfiguration: { adhocCreditRequest, arrivals, productLabelAdhocPrint, transfers, outstandingEdiInvoices },
		badge,
	} = pageContext;
	const [isReceiveOpen, setReceiveOpen] = React.useState(false);
	const [isCreditRequestOpen, setCreditRequestOpen] = React.useState(false);
	const [isTransfersOpen, setIsTransfersOpen] = React.useState(false);
	const [isLabelsOpen, setIsLabelsOpen] = React.useState(false);
	const classes = useStyles();
	const [selectedIndex, setSelectedIndex] = React.useState<string>("home");

	const isArrivalsFeatureEnabled = () => {
		return isFeatureEnabledForLocation(userLocation, arrivals);
	};

	const isAdhocCreditRequestFeatureEnabled = () => {
		return isFeatureEnabledForLocation(userLocation, adhocCreditRequest);
	};

	const isProductLabelPrintingAdhocFeatureEnabled = () => {
		return isFeatureEnabledForLocation(userLocation, productLabelAdhocPrint);
	};

	const isTransfersEnabled = () => {
		return isFeatureEnabledForLocation(userLocation, transfers);
	};

	const isOutstandingEdiInvoicesFeatureEnabled = () => {
		return isFeatureEnabledForLocation(userLocation, outstandingEdiInvoices);
	};

	const filteredReceives = isArrivalsFeatureEnabled()
		? badge.receives.filter((r) => r.status !== receiveStatus.pendingFinalise)
		: badge.receives.filter((r) => r.status !== receiveStatus.arrived && r.status !== receiveStatus.pendingFinalise);
	const receiveFailuresTotal = sumArray(badge.receiveFailures, "count");
	const receiveTotal = sumArray(filteredReceives, "count") + receiveFailuresTotal;
	const filteredCreditRequests = isAdhocCreditRequestFeatureEnabled()
		? badge.creditRequests.filter((cr) => cr.status !== creditStatus.cancelled)
		: badge.creditRequests.filter(
				(cr) => cr.status !== creditStatus.cancelled && cr.status !== creditStatus.awaitingSupplier
			);
	const creditRequestTotal = sumArray(filteredCreditRequests, "count");
	const transferTotal = getTransferBadgeCount(badge.transfers);

	const onListItemClick = (e, index) => {
		setSelectedIndex(index);
		onMenuItemSelected(index);
	};

	const handleReceiveClick = () => {
		setReceiveOpen(!isReceiveOpen);
	};
	const handleCreditRequestClick = () => {
		setCreditRequestOpen(!isCreditRequestOpen);
	};
	const handleTransfersClick = () => {
		setIsTransfersOpen(!isTransfersOpen);
	};

	const handleLabelsClick = () => {
		setIsLabelsOpen(!isLabelsOpen);
	};

	const getReceivingClass = () => {
		if (isReceiveOpen) {
			return classes.shadowDown;
		}
		return undefined;
	};
	const getCreditRequestClass = () => {
		if (isReceiveOpen && isCreditRequestOpen) {
			return classes.shadowUpAndDown;
		}
		if (isReceiveOpen) {
			return classes.shadowUp;
		}
		if (isCreditRequestOpen) {
			return classes.shadowDown;
		}
		return undefined;
	};
	const getTransferClass = () => {
		if (isTransfersOpen && isCreditRequestOpen) {
			return classes.shadowUpAndDown;
		}
		if (isCreditRequestOpen) {
			return classes.shadowUp;
		}
		if (isTransfersOpen) {
			return classes.shadowDown;
		}
		return undefined;
	};

	return (
		<React.Fragment>
			<Drawer open={isOpenMenu} onClose={OnDrawerClose}>
				<List className={classes.root}>
					<ListItemButton key="Home" selected={selectedIndex === "home"} onClick={(e) => onListItemClick(e, "home")}>
						<ListItemIcon className={classes.menuIcon}>
							<HomeIcon />
						</ListItemIcon>
						<ListItemText primary="Home" />
					</ListItemButton>
					{isArrivalsFeatureEnabled() && (
						<ListItemButton
							key="Checkin"
							selected={selectedIndex === "checkin"}
							onClick={(e) => onListItemClick(e, "checkin")}
						>
							<ListItemIcon className={classes.menuIcon}>
								<CheckinIcon />
							</ListItemIcon>
							<ListItemText primary="Checkin" />
						</ListItemButton>
					)}
					<ListItemButton className={getReceivingClass()} onClick={handleReceiveClick} key="Receiving">
						<ListItemIcon className={classes.menuIcon}>
							{!isReceiveOpen && (
								<Badge overlap="rectangular" className={classes.badge} badgeContent={receiveTotal} color="error">
									<AssignmentIcon />
								</Badge>
							)}
							{isReceiveOpen && <AssignmentIcon />}
						</ListItemIcon>
						<ListItemText primary="Purchase Orders" />
						{isReceiveOpen ? <ExpandLess /> : <ExpandMore />}
					</ListItemButton>
					<Collapse in={isReceiveOpen} timeout="auto" unmountOnExit>
						{isArrivalsFeatureEnabled() && (
							<BadgedMenuItem
								keyPrefix={`receives`}
								keyName={receiveStatus.arrived}
								icon={<FlightLandIcon />}
								badgeList={badge.receives}
								badgeListKey="status"
								badgeListValue="count"
								selectedIndex={selectedIndex}
								onClick={onListItemClick}
								title="Arrived"
							/>
						)}
						<BadgedMenuItem
							keyPrefix={`receives`}
							keyName={receiveStatus.pendingReview}
							icon={<RateReviewIcon />}
							badgeList={badge.receives}
							badgeListKey="status"
							badgeListValue="count"
							selectedIndex={selectedIndex}
							onClick={onListItemClick}
							title="Manuals Pending Review"
						/>
						<BadgedMenuItem
							keyPrefix={`receives`}
							keyName={receiveStatus.reviewing}
							icon={<PeopleIcon />}
							badgeList={badge.receives}
							badgeListKey="status"
							badgeListValue="count"
							selectedIndex={selectedIndex}
							onClick={onListItemClick}
							title="Manuals In Review"
						/>
						<BadgedMenuItem
							keyPrefix={`receives`}
							keyName={receiveStatus.inProgress}
							icon={<PlayCircleFilledWhiteIcon />}
							badgeList={badge.receives}
							badgeListKey="status"
							badgeListValue="count"
							selectedIndex={selectedIndex}
							onClick={onListItemClick}
							title="Continue In Progress"
						/>
						<BadgedMenuItem
							keyPrefix={`receives`}
							keyName={receiveStatus.pendingReceive}
							icon={<PendingActionIcon />}
							badgeList={badge.receives}
							badgeListKey="status"
							badgeListValue="count"
							selectedIndex={selectedIndex}
							onClick={onListItemClick}
							title="Pending Receive"
						/>
						<BadgedMenuItem
							keyPrefix={`receives`}
							keyName={receiveFailureReason.ediInvoiceMissing}
							icon={<AccessAlarmIcon />}
							badgeList={badge.receiveFailures}
							badgeListKey="failureReason"
							badgeListValue="count"
							selectedIndex={selectedIndex}
							onClick={onListItemClick}
							title="Receives Missing EDI"
						/>
						<BadgedMenuItem
							keyPrefix={`receives`}
							keyName={failureReasonsRequiringInterventionCsv}
							linkSuffix={receiveStatus.pendingFinalise}
							icon={<ErrorIcon />}
							badgeList={badge.receiveFailures}
							badgeListKey="failureReason"
							badgeListValue="count"
							selectedIndex={selectedIndex}
							onClick={onListItemClick}
							title="Issues"
						/>
					</Collapse>
					<ListItemButton className={getCreditRequestClass()} onClick={handleCreditRequestClick} key="CreditRequest">
						<ListItemIcon className={classes.menuIcon}>
							{!isCreditRequestOpen && (
								<Badge overlap="rectangular" className={classes.badge} badgeContent={creditRequestTotal} color="error">
									<AttachMoneyIcon />
								</Badge>
							)}
							{isCreditRequestOpen && <AttachMoneyIcon />}
						</ListItemIcon>
						<ListItemText primary="Credit Request" />
						{isCreditRequestOpen ? <ExpandLess /> : <ExpandMore />}
					</ListItemButton>
					<Collapse in={isCreditRequestOpen} timeout="auto" unmountOnExit>
						<ListItemButton
							className={classes.nested}
							key="creditRequestSearch"
							selected={selectedIndex === "creditRequestSearch"}
							onClick={(e) => onListItemClick(e, "creditRequestSearch")}
						>
							<ListItemIcon className={classes.menuIcon}>
								<CreditCard />
							</ListItemIcon>
							<ListItemText primary="Search" />
						</ListItemButton>
						{isAdhocCreditRequestFeatureEnabled() && (
							<ListItemButton
								className={classes.nested}
								key="adhocCredit"
								selected={selectedIndex === "adhocCredit"}
								onClick={(e) => onListItemClick(e, "adhocCredit")}
							>
								<ListItemIcon className={classes.menuIcon}>
									<AddCircleIcon />
								</ListItemIcon>
								<ListItemText primary="Create" />
							</ListItemButton>
						)}
						{isAdhocCreditRequestFeatureEnabled() && (
							<BadgedMenuItem
								keyPrefix={`credits`}
								keyName={creditStatus.awaitingSupplier}
								icon={<PeopleAltIcon />}
								badgeList={badge.creditRequests}
								badgeListKey="status"
								badgeListValue="count"
								selectedIndex={selectedIndex}
								onClick={onListItemClick}
								title="Awaiting Supplier"
							/>
						)}
						<BadgedMenuItem
							keyPrefix={`credits`}
							keyName={creditStatus.pendingReview}
							icon={<AssignmentTurnedInIcon />}
							badgeList={badge.creditRequests}
							badgeListKey="status"
							badgeListValue="count"
							selectedIndex={selectedIndex}
							onClick={onListItemClick}
							title="Pending Review"
						/>
						<BadgedMenuItem
							keyPrefix={`credits`}
							keyName={creditStatus.reviewing}
							icon={<PeopleIcon />}
							badgeList={badge.creditRequests}
							badgeListKey="status"
							badgeListValue="count"
							selectedIndex={selectedIndex}
							onClick={onListItemClick}
							title="Reviewing"
						/>
					</Collapse>
					{isTransfersEnabled() && (
						<>
							<ListItemButton className={getTransferClass()} onClick={handleTransfersClick} key="Transfers">
								<ListItemIcon className={classes.menuIcon}>
									{!isTransfersOpen && (
										<Badge overlap="rectangular" className={classes.badge} badgeContent={transferTotal} color="error">
											<SyncAltIcon />
										</Badge>
									)}
									{isTransfersOpen && <SyncAltIcon />}
								</ListItemIcon>
								<ListItemText primary="Transfers" />
								{isTransfersOpen ? <ExpandLess /> : <ExpandMore />}
							</ListItemButton>
							<Collapse in={isTransfersOpen} timeout="auto" unmountOnExit>
								<ListItemButton
									className={classes.nested}
									key="transfersIncoming"
									selected={selectedIndex === "transfersIncoming"}
									onClick={(e) => onListItemClick(e, "transfersIncoming")}
								>
									<ListItemIcon className={classes.menuIcon}>
										<ThumbsUpAltIcon />
									</ListItemIcon>
									<ListItemText primary="Incoming Transfers" />
								</ListItemButton>
								<BadgedMenuItem
									keyPrefix="transfers"
									keyName="InProgress"
									icon={<PlayCircleFilledWhiteIcon />}
									badgeList={badge.transfers}
									badgeListKey="status"
									badgeListValue="count"
									selectedIndex={selectedIndex}
									onClick={(e) => onListItemClick(e, "transfersContinueInProgress")}
									title="Continue In Progress"
								></BadgedMenuItem>
								<BadgedMenuItem
									keyPrefix="transfers"
									keyName="PendingReceive"
									icon={<PendingActionIcon />}
									badgeList={badge.transfers}
									badgeListKey="status"
									badgeListValue="count"
									selectedIndex={selectedIndex}
									onClick={(e) => onListItemClick(e, "transfersPendingReceive")}
									title="Pending Receive"
								></BadgedMenuItem>
								<BadgedMenuItem
									keyPrefix="transfers"
									keyName={transferBadgeStatus.outstandingVirtuals}
									icon={<SmartToyOutlinedIcon />}
									badgeList={badge.transfers}
									badgeListKey="status"
									badgeListValue="count"
									title="Virtual Transfers"
									onClick={onListItemClick}
									selectedIndex={selectedIndex}
								/>
							</Collapse>
						</>
					)}
					{isProductLabelPrintingAdhocFeatureEnabled() && (
						<>
							<ListItemButton key="Labels" onClick={handleLabelsClick}>
								<ListItemIcon className={classes.menuIcon}>
									<Label />
								</ListItemIcon>
								<ListItemText primary="Labels" />
								{isLabelsOpen ? <ExpandLess /> : <ExpandMore />}
							</ListItemButton>
							<Collapse in={isLabelsOpen} timeout="auto" unmountOnExit>
								<ListItemButton
									className={classes.nested}
									key="labels"
									selected={selectedIndex === "labels"}
									onClick={(e) => onListItemClick(e, "labels")}
								>
									<ListItemIcon className={classes.menuIcon}>
										<Label />
									</ListItemIcon>
									<ListItemText primary="Product Labels" />
								</ListItemButton>
								<ListItemButton
									className={classes.nested}
									key="tagSheetLabels"
									selected={selectedIndex === "tagSheetLabels"}
									onClick={(e) => onListItemClick(e, "tagSheetLabels")}
								>
									<ListItemIcon className={classes.menuIcon}>
										<Label />
									</ListItemIcon>
									<ListItemText primary="Tag Sheet Labels" />
								</ListItemButton>
							</Collapse>
						</>
					)}

					<ListItemButton key="ASNs" selected={selectedIndex === "asn"} onClick={(e) => onListItemClick(e, "asn")}>
						<ListItemIcon className={classes.menuIcon}>
							<LocalShippingIcon />
						</ListItemIcon>
						<ListItemText primary="ASNs" />
					</ListItemButton>
					{isOutstandingEdiInvoicesFeatureEnabled() && (
						<>
							<BadgedItem
								keyName="OutstandingEdiInvoices"
								title="Outstanding EDI Invoices"
								badgeCount={badge.ediOutstanding}
								selectedIndex={selectedIndex}
								onClick={(e) => onListItemClick(e, "outstandingEdiInvoices")}
								icon={<PendingActionIcon />}
							/>
						</>
					)}
					<ListItemButton
						key="Settings"
						selected={selectedIndex === "settings"}
						onClick={(e) => onListItemClick(e, "settings")}
					>
						<ListItemIcon className={classes.menuIcon}>
							<SettingsIcon />
						</ListItemIcon>
						<ListItemText primary="Settings" />
					</ListItemButton>
				</List>
			</Drawer>
		</React.Fragment>
	);
};
